import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { MessageSquare, X, Send, Sparkles, Loader2, Mail, Phone } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const newSessionId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const WHATSAPP_LINK = 'https://wa.me/447950523316';
const WHATSAPP_DISPLAY = '+44 7950 523316';
const EMAIL = 'shopperden@yahoo.com';

// Routes where the bubble should NOT appear (dashboards get their own help surfaces,
// auth flows need minimal UI, admin doesn't need a public widget).
const HIDE_ON = [
  /^\/contact/,            // ContactPage has its own chat
  /^\/admin/,
  /^\/login/,
  /^\/register/,
  /^\/forgot-password/,
  /^\/photo-bridge/,       // mobile camera flow — no chrome
];

const INITIAL_MSG = {
  role: 'assistant',
  text: "Hi! I'm Shopperden AI. Ask me about pricing, listings, subscriptions or how the platform works — I'll reply instantly.",
};

const FloatingAIChat = () => {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(newSessionId);
  const [messages, setMessages] = useState([INITIAL_MSG]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [unread, setUnread] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const path = typeof window !== 'undefined' ? window.location.pathname : '/';
  const hide = HIDE_ON.some((re) => re.test(path));

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy, open]);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200);
      setUnread(false);
    }
  }, [open]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || busy) return;
    setMessages((m) => [...m, { role: 'user', text }]);
    setInput('');
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/contact/ai-chat`, {
        message: text,
        session_id: sessionId,
      });
      setMessages((m) => [...m, { role: 'assistant', text: r.data.reply }]);
      if (!open) setUnread(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || "I couldn't reply just now — please email shopperden@yahoo.com.";
      setMessages((m) => [...m, { role: 'assistant', text: msg }]);
    } finally {
      setBusy(false);
    }
  }, [input, busy, sessionId, open]);

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (hide) return null;

  return (
    <>
      {/* Launcher bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Open Shopperden AI chat"
          className="fixed z-[60] group flex items-center gap-2.5 bg-gradient-to-br from-[#10B981]/85 to-[#0F766E]/85 hover:from-[#10B981] hover:to-[#0F766E] backdrop-blur-md text-white shadow-xl transition-all duration-200 hover:scale-105 opacity-80 hover:opacity-100
            bottom-20 right-3 w-11 h-11 rounded-full justify-center
            sm:bottom-5 sm:right-5 sm:w-auto sm:h-auto sm:pl-3 sm:pr-4 sm:py-3 sm:rounded-full sm:justify-start sm:opacity-90"
          data-testid="floating-ai-bubble"
        >
          <span className="relative w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
            <Sparkles className="h-4 w-4" />
            {unread && (
              <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#E07A3A] border-2 border-white" aria-hidden="true" />
            )}
          </span>
          <span className="hidden sm:flex flex-col items-start leading-tight">
            <span className="text-[11px] tracking-widest uppercase text-white/70">Need help?</span>
            <span className="text-sm font-semibold">Ask Shopperden AI</span>
          </span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed z-[60] flex flex-col bg-[#0F172A] text-white shadow-2xl border border-white/10 rounded-2xl overflow-hidden
            inset-x-3 bottom-3 top-3
            sm:inset-auto sm:bottom-4 sm:right-4 sm:top-auto sm:w-[380px] sm:h-[560px] sm:max-h-[calc(100vh-2rem)]"
          role="dialog"
          aria-label="Shopperden AI chat"
          data-testid="floating-ai-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-br from-[#0F172A] to-[#1E293B] border-b border-white/10">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#10B981] to-[#0F766E] flex items-center justify-center">
                <Sparkles className="h-4 w-4 text-white" />
              </div>
              <div className="leading-tight">
                <p className="font-['Playfair_Display'] text-base">Shopperden AI</p>
                <p className="text-[11px] text-emerald-300 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Online · Instant replies
                </p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-1.5 rounded-md hover:bg-white/10 transition-colors"
              aria-label="Close chat"
              data-testid="floating-ai-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
            data-testid="floating-ai-messages"
          >
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] px-3.5 py-2.5 text-[13.5px] leading-relaxed rounded-2xl whitespace-pre-wrap ${
                    m.role === 'user'
                      ? 'bg-[#E07A3A] text-white rounded-br-sm'
                      : 'bg-white/10 text-white/90 rounded-bl-sm'
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-white/10 text-white/70 px-3.5 py-2.5 rounded-2xl rounded-bl-sm text-sm flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking…
                </div>
              </div>
            )}
          </div>

          {/* Escalation bar */}
          <div className="px-3 py-2 flex items-center gap-2 border-t border-white/10 bg-white/[0.03]">
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-[11px] text-emerald-300 hover:text-emerald-200 tracking-wider px-2 py-1 rounded-md hover:bg-white/5 transition-colors"
              data-testid="floating-ai-whatsapp"
              title={WHATSAPP_DISPLAY}
            >
              <Phone className="h-3 w-3" /> WhatsApp
            </a>
            <a
              href={`mailto:${EMAIL}`}
              className="flex items-center gap-1.5 text-[11px] text-white/60 hover:text-white/90 tracking-wider px-2 py-1 rounded-md hover:bg-white/5 transition-colors"
              data-testid="floating-ai-email"
            >
              <Mail className="h-3 w-3" /> Email
            </a>
            <span className="ml-auto text-[10px] text-white/30 tracking-wider uppercase">AI · informational</span>
          </div>

          {/* Composer */}
          <div className="p-3 border-t border-white/10 bg-[#0F172A]">
            <div className="flex gap-2 items-end">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder="Ask a question…"
                rows={1}
                maxLength={1500}
                className="flex-1 resize-none bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#10B981] max-h-24"
                data-testid="floating-ai-input"
              />
              <button
                onClick={send}
                disabled={busy || !input.trim()}
                className="bg-[#E07A3A] hover:bg-[#E07A3A]/90 disabled:opacity-40 disabled:cursor-not-allowed text-white p-2.5 rounded-lg transition-colors"
                aria-label="Send message"
                data-testid="floating-ai-send"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export { MessageSquare };
export default FloatingAIChat;
